import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { PeriodField } from './period/PeriodPicker';
import { StatSummary } from './ui/StatSummary';
import { dateToStoreISO } from '../utils/timezones';
import { getErrorMessage } from '../api/client';
import { getShiftDailySummary } from '../api/shifts';
import { useAuth } from '../auth/AuthProvider';
import { getStoreScopeOptions } from '../auth/permissions';
import type { ShiftDailyStoreSummary } from '../types/shifts';
import type { Store } from '../types/stores';
import { formatOptionalMoney } from '../utils/costFormatting';

function todayLocalDate() { return dateToStoreISO(new Date()); }

interface DailySummaryTotals {
  activeCloses: number;
  voidedCloses: number;
  grossSales: string;
  expectedCash: string | null;
  countedCash: string;
  cashDifference: string | null;
  totalReceipts: number;
}

function sumTotals(stats: ShiftDailyStoreSummary[]): DailySummaryTotals {
  const grossSales = stats.reduce((sum, store) => sum + Number(store.grossSales), 0);
  const countedCash = stats.reduce((sum, store) => sum + Number(store.countedCash), 0);
  const withExpectedCash = stats.filter((store) => store.expectedCash !== null);
  const expectedCash = withExpectedCash.length ? withExpectedCash.reduce((sum, store) => sum + Number(store.expectedCash), 0) : null;
  return {
    activeCloses: stats.reduce((sum, store) => sum + store.activeCloses, 0),
    voidedCloses: stats.reduce((sum, store) => sum + store.voidedCloses, 0),
    grossSales: grossSales.toFixed(2),
    expectedCash: expectedCash === null ? null : expectedCash.toFixed(2),
    countedCash: countedCash.toFixed(2),
    cashDifference: expectedCash === null ? null : (countedCash - expectedCash).toFixed(2),
    totalReceipts: stats.reduce((sum, store) => sum + store.totalReceipts, 0),
  };
}

// `actions`: lo que va a la derecha del título (la pantalla pone ahí «Ver Reportes»).
export function ShiftDailySummaryPanel({ stores, refreshToken, actions }: { stores: Store[]; refreshToken: number; actions?: ReactNode }) {
  const { user } = useAuth();
  const [date, setDate] = useState(todayLocalDate());
  const [storeId, setStoreId] = useState('');
  const [stats, setStats] = useState<ShiftDailyStoreSummary[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  // P-075: solo la respuesta de la ÚLTIMA consulta se pinta (la primera sale antes de que lleguen las
  // tiendas y su error podía llegar después de la buena).
  const loadSeq = useRef(0);
  const load = useCallback(async (nextDate: string, nextStoreId: string) => {
    if (!nextDate) return;
    const current = ++loadSeq.current;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getShiftDailySummary({ date: nextDate, storeId: nextStoreId || undefined });
      if (current === loadSeq.current) setStats(result.stores);
    } catch (loadError) {
      if (current === loadSeq.current) setError(getErrorMessage(loadError, 'No fue posible cargar el resumen diario.'));
    } finally {
      if (current === loadSeq.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const { defaultStoreId } = getStoreScopeOptions(user?.role, stores);
    if (defaultStoreId) setStoreId(defaultStoreId);
    void load(date, defaultStoreId ?? storeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshToken, stores, user?.role]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void load(date, storeId);
  };

  const totals = stats && stats.length > 0 ? sumTotals(stats) : undefined;
  const hasAnyOverlap = stats?.some((store) => store.hasOverlaps) ?? false;

  return <section className="preview-section shift-daily-summary-section">
    <div className="preview-section__heading"><div><h3>Resumen diario</h3><span>Operación de cortes de turno por tienda para un día específico.</span></div>{actions}</div>
    {error && <div className="alert alert--error">{error}</div>}

    <form className="stock-filters" onSubmit={submit}>
      {/* P-075: el selector global de periodos en modo día (Hoy · Ayer · Antier o un día del calendario), con
          el mismo «hoy» de siempre. Elegir el día no consulta: «Consultar» sigue pidiendo día + tienda. Sin
          tope de días futuros, como el calendario anterior. */}
      <PeriodField allowFuture label="Fecha" mode="day" onChange={(range) => setDate(range.from)} today={todayLocalDate()} value={{ from: date, to: date }} />
      <label className="field"><span>Tienda</span><select onChange={(event) => setStoreId(event.target.value)} value={storeId}>{getStoreScopeOptions(user?.role, stores).allowsAllStores && <option value="">Todas las tiendas</option>}{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
      <button className="button button--primary" disabled={isLoading || !date} type="submit">{isLoading ? 'Cargando...' : 'Consultar'}</button>
    </form>

    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando resumen diario...</div>}
    {!isLoading && stats && stats.length === 0 && <div className="empty-panel empty-panel--small"><strong>Sin cortes ese día</strong><span>No hay cortes de turno guardados para la fecha seleccionada.</span></div>}

    {!isLoading && totals && <>
      {/* P-076: cuatro cifras primero (venta, esperado, contado, diferencia) y las tres de conteo plegadas; antes eran
          siete tarjetas apiladas (~700 px) antes de la tabla. */}
      <StatSummary
        label="Resumen diario general"
        more={[
          { label: 'Cortes activos', value: totals.activeCloses },
          { label: 'Cortes anulados', value: totals.voidedCloses },
          { label: 'Tickets', value: totals.totalReceipts },
        ]}
        moreLabel="Ver cortes y tickets"
        stats={[
          { label: 'Venta bruta', value: formatOptionalMoney(totals.grossSales) },
          { label: 'Efectivo esperado', value: formatOptionalMoney(totals.expectedCash) },
          { label: 'Efectivo contado', value: formatOptionalMoney(totals.countedCash) },
          { label: 'Diferencia total', value: formatOptionalMoney(totals.cashDifference), tone: totals.cashDifference === null ? 'default' : Number(totals.cashDifference) < 0 ? 'danger' : Number(totals.cashDifference) > 0 ? 'warning' : 'default' },
        ]}
      />

      {hasAnyOverlap && <div className="alert alert--error">Se detectó un traslape entre cortes activos de una tienda (no debería ocurrir).</div>}

      {/* P-076: tabla analítica compacta (se comparan tiendas): identidad fija y desplazamiento local en el teléfono. */}
      {stats && stats.length > 1 && <div className="data-table-wrap"><table className="data-table n3-table--analytic"><thead><tr>
        <th>Tienda</th><th>Cortes activos</th><th>Cortes anulados</th><th>Venta bruta</th><th>Efectivo esperado</th><th>Efectivo contado</th><th>Diferencia</th><th>Tickets</th>
      </tr></thead><tbody>{stats.map((store) => <tr key={store.storeId}>
        <td>{store.storeName}</td>
        <td>{store.activeCloses}</td>
        <td>{store.voidedCloses}</td>
        <td>{formatOptionalMoney(store.grossSales)}</td>
        <td>{formatOptionalMoney(store.expectedCash)}</td>
        <td>{formatOptionalMoney(store.countedCash)}</td>
        <td>{formatOptionalMoney(store.cashDifference)}</td>
        <td>{store.totalReceipts}</td>
      </tr>)}</tbody></table></div>}

      {stats && stats.some((store) => store.hasOpenGaps) && <div className="alert alert--warning">Hay al menos una tienda con huecos sin cubrir por un corte activo ese día.</div>}
    </>}
  </section>;
}
